import React from 'react';
import { Card } from '../ui/card';
import { CandidateJourneyCompact } from '../CandidateJourney';
import { getScoreClass, getFitColor } from '../../lib/scoreUtils';

// A single ranked candidate row in the Job Detail candidate list
export function CandidateRankingRow({ candidate: c, rank, onOpen, onSendAssessment, onVerify, blendMode = false, blendedScore = null, hasFit = false }) {
  const assessmentDone = (c.assessment_attempts || []).some(a => a.status === 'scored' || a.status === 'completed');
  const interviewDone = (c.interview_sessions || []).some(s => s.status === 'scored' || s.status === 'completed');
  const missingCount = (c.match_result?.missing_skills || []).length;
  const redFlagCount = (c.match_result?.red_flags || []).length;

  return (
    <Card
      data-testid="candidate-ranking-row"
      onClick={() => onOpen(c)}
      style={{
        background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12,
        padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem',
        animation: 'fadeInUp 0.22s ease-out',
      }}
      className="candidate-row"
    >
      <div style={{ width: 28, fontFamily: 'Cabinet Grotesk', fontWeight: 700, fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', textAlign: 'center' }}>
        #{rank + 1}
      </div>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: `linear-gradient(135deg, ${rank === 0 ? '#10B981, #0076A3' : rank < 3 ? '#0076A3, #0076A3' : '#0076A3, #2E3B3D'})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.8125rem', fontWeight: 600, color: '#fff', flexShrink: 0
      }}>
        {c.name?.charAt(0)?.toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span data-testid="candidate-ranking-name" style={{ fontSize: '0.9375rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
          {c.applied_by_candidate && (
            <span style={{ padding: '0.125rem 0.375rem', borderRadius: 999, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', fontSize: '0.5625rem', fontWeight: 600, color: '#059669', whiteSpace: 'nowrap' }}>Self-Applied</span>
          )}
          {assessmentDone && (
            <span title="Assessment Completed" style={{ display: 'inline-flex', alignItems: 'center', padding: '0.125rem 0.375rem', borderRadius: 999, background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)', fontSize: '0.5625rem', fontWeight: 600, color: '#F2600C', whiteSpace: 'nowrap' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 2 }}>
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/><path d="M9 14l2 2 4-4"/>
              </svg>
              Assessment
            </span>
          )}
          {interviewDone && (
            <span title="Interview Completed" style={{ display: 'inline-flex', alignItems: 'center', padding: '0.125rem 0.375rem', borderRadius: 999, background: 'rgba(30,104,255,0.1)', border: '1px solid rgba(30,104,255,0.2)', fontSize: '0.5625rem', fontWeight: 600, color: '#0076A3', whiteSpace: 'nowrap' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 2 }}>
                <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
              </svg>
              Interview
            </span>
          )}
          {c.bgv_documents_count > 0 && (
            <span title="BGV Documents Submitted" style={{ display: 'inline-flex', alignItems: 'center', padding: '0.125rem 0.375rem', borderRadius: 999, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', fontSize: '0.5625rem', fontWeight: 600, color: '#059669', whiteSpace: 'nowrap' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 2 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
              BGV
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {c.parsed_resume?.email || '—'}{c.parsed_resume?.phone ? ` · ${c.parsed_resume.phone}` : ''}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <span style={{ fontWeight: 600, color: getFitColor(c.fit_recommendation) }} data-testid="candidate-fit-label">{c.fit_recommendation}</span>
          {(c.match_result?.matched_skills || []).length ? <> &middot; {(c.match_result?.matched_skills || []).slice(0, 3).join(', ')}</> : null}
        </div>
        {(c.verified_skills || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
            {(c.verified_skills || []).slice(0, 4).map((s) => (
              <span key={s} data-testid="candidate-verified-skill"
                title={`Proven in assessment (scored ≥80%): ${s}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.5625rem', fontWeight: 700, color: '#047857', background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.28)', borderRadius: 999, padding: '0.1rem 0.45rem' }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {s}
              </span>
            ))}
            {(c.verified_skills || []).length > 4 && (
              <span data-testid="candidate-verified-skill-more" title={(c.verified_skills || []).join(', ')}
                style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#047857', background: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.2)', borderRadius: 999, padding: '0.1rem 0.4rem' }}>
                +{(c.verified_skills || []).length - 4} verified
              </span>
            )}
          </div>
        )}
        {c.journey && <div style={{ marginTop: '0.5rem', maxWidth: 220 }}><CandidateJourneyCompact journey={c.journey} /></div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        {c.behavioral_fit && (() => {
          const b = c.behavioral_fit.band;
          const col = b === 'Strong' ? '#059669' : b === 'Moderate' ? '#D97706' : '#DC2626';
          const bg = b === 'Strong' ? 'rgba(5,150,105,0.1)' : b === 'Moderate' ? 'rgba(217,119,6,0.1)' : 'rgba(220,38,38,0.1)';
          return (
            <span data-testid="candidate-behavioral-fit-badge"
              title={`Psychometric role fit: ${b}${c.behavioral_style ? ` · ${c.behavioral_style}` : ''}`}
              style={{ fontSize: '0.625rem', fontWeight: 700, borderRadius: 999, padding: '0.15rem 0.5rem', whiteSpace: 'nowrap', color: col, background: bg, border: `1px solid ${col}33`, cursor: 'help' }}>
              Fit {Math.round(c.behavioral_fit.score)}{c.behavioral_style ? ` · ${c.behavioral_style}` : ''}
            </span>
          );
        })()}
        {redFlagCount > 0 && (
          <span data-testid="candidate-red-flag-chip" title={(c.match_result?.red_flags || []).join('; ')} style={{ fontSize: '0.6875rem', color: '#DC2626', fontWeight: 700, cursor: 'help' }}>
            ⚠ {redFlagCount}
          </span>
        )}
        {missingCount > 0 && (
          <span style={{ fontSize: '0.6875rem', color: '#F59E0B' }}>
            {missingCount} gaps
          </span>
        )}
        <button
          data-testid="send-assessment-button"
          onClick={(e) => { e.stopPropagation(); onSendAssessment(c); }}
          title="Send Assessment"
          style={{ padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid rgba(124,58,237,0.2)', background: 'rgba(124,58,237,0.06)', cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: '#0076A3', whiteSpace: 'nowrap' }}
        >
          Assessment
        </button>
        <button
          data-testid="verify-candidate-button"
          onClick={(e) => { e.stopPropagation(); onVerify(c); }}
          title="Start Verification"
          style={{ padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid #CBD5E1', background: '#FAF7F2', cursor: 'pointer', fontSize: '0.625rem', fontWeight: 600, color: '#0076A3', whiteSpace: 'nowrap' }}
        >
          Verify
        </button>
        {blendMode && hasFit && (
          <span data-testid="candidate-blended-score-badge" title="Blended ranking: skills score combined with psychometric culture fit"
            style={{ fontSize: '0.7rem', fontWeight: 800, borderRadius: 8, padding: '0.15rem 0.5rem', color: '#fff', background: '#0076A3', whiteSpace: 'nowrap' }}>
            ◆ {blendedScore}
          </span>
        )}
        <span className={`score-badge ${getScoreClass(c.overall_score)}`} data-testid="candidate-score-badge" title={blendMode ? 'Skills score' : 'Match score'}>
          {c.overall_score}
        </span>
      </div>
    </Card>
  );
}
